import axiosMiddleware from "redux-axios-middleware";
import axios from "axios/index";
import { applyMiddleware, createStore, compose } from "redux";
import rootReducer from "./ReducerCombine";
import { namedReducerEnhancer } from "redux-named-reducers";
import { envConfig } from "../config";
import createAuthRefreshInterceptor from "axios-auth-refresh";
const ACCESS_TOKEN = "access_token";
const REFRESH_TOKEN = "refresh_token";

export const axios_client = axios.create({
  baseURL: envConfig.REACT_APP_BASEURL,
  responseType: "json",
  timeout: 300000, // Let's say you want to wait at least 180 seconds
});

// Define the status codes that should trigger token refresh
const REFRESH_TRIGGER_STATUSES = [401];

// Function to refresh token when 401 error occurs
const refreshAuthLogic = async (failedRequest) => {
  const requestUrl = failedRequest.response.config.url;

  // Don't refresh tokens for unauthenticated endpoints
  const requestMethod = failedRequest.response.config.method?.toLowerCase();
  const unauthenticatedEndpoints = [
    { url: "/oauth", matchType: "exact" },
    { url: "/oauth/refresh", matchType: "exact" },
    { url: "/users", matchType: "exact", method: "post" },
    { url: "/users/forgot_password", matchType: "exact" },
    { url: "/users/password_reset", matchType: "exact" },
    { url: "/users/verify_email_or_phone", matchType: "startsWith" },
    { url: "/companies", matchType: "exact", method: "post" },
  ];
  const isUnauthenticated = unauthenticatedEndpoints.some((endpoint) => {
    const urlMatch =
      endpoint.matchType === "exact"
        ? requestUrl === endpoint.url
        : requestUrl.startsWith(endpoint.url);
    const methodMatch = endpoint.method
      ? requestMethod === endpoint.method
      : true;
    return urlMatch && methodMatch;
  });

  if (isUnauthenticated) {
    return Promise.reject(failedRequest);
  }

  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);

    if (!refreshToken) {
      // No refresh token means user isn't logged in — don't redirect, just reject
      return Promise.reject(failedRequest);
    }

    // Send POST request to refresh token with refreshToken in body
    // skipAuthRefresh prevents createAuthRefreshInterceptor from intercepting
    // a 401 on this request, which would cause a deadlock
    const response = await axios_client.post(
      `/oauth/refresh`,
      { refresh_token: refreshToken },
      { skipAuthRefresh: true },
    );

    const newAccessToken = response.data.oauth.token;

    if (!newAccessToken) {
      throw new Error("No access token received from refresh");
    }

    // Store new access token in localStorage
    localStorage.setItem(ACCESS_TOKEN, `Bearer ${newAccessToken}`);

    // Update the axios default headers for future requests
    axios_client.defaults.headers.common["Authorization"] =
      `Bearer ${newAccessToken}`;

    // Update failed request with new token
    failedRequest.response.config.headers.Authorization = `Bearer ${newAccessToken}`;

    return Promise.resolve();
  } catch (error) {
    window.location.href = "/logout";
    return Promise.reject(error);
  }
};

// 🔹 Attach the auth refresh interceptor to both instances
createAuthRefreshInterceptor(axios_client, refreshAuthLogic, {
  statusCodes: REFRESH_TRIGGER_STATUSES,
});

axios_client.interceptors.request.use(
  (configuration) => {
    // Always use the freshest token from localStorage
    const token = localStorage.getItem(ACCESS_TOKEN);

    if (token) {
      configuration.headers.Authorization = token;
    }

    //exclude our signature from Paystack requests
    // if (!configuration.baseURL?.includes(envConfig.REACT_APP_PAYSTACK_URL)) {
    configuration.headers["x-gotruck-signature"] =
      envConfig.REACT_APP_GOTRUCK_SIGNATURE;
    // }

    return configuration;
  },

  (error) => Promise.reject(error),
);

axios_client.interceptors.response.use(
  function (response) {
    return response;
  },
  (error) => {
    // Only clear tokens for specific 401 errors that aren't being handled by refresh interceptor
    if (error && error.response && error.response.status === 401) {
      if (error.response.data?.status === 4011) {
        // Only clear if this is not a token refresh request
        if (!error.config?.url?.includes("/oauth/refresh")) {
          delete axios_client.defaults.headers.common["Authorization"];
          localStorage.removeItem(ACCESS_TOKEN);
        }
      }
    }
    return Promise.reject(error);
  },
);

const middlewareConfig = {
  returnRejectedPromiseOnError: true,
};

const store = createStore(
  rootReducer,
  compose(
    namedReducerEnhancer(rootReducer),
    applyMiddleware(axiosMiddleware(axios_client, middlewareConfig)),
  ),
);

export default store;
